/**
 * A pathname reduced to a low-cardinality template, for the `route` property every Kelly event carries
 * ("which page was Kelly open beside"). Raw pathnames are unusable as an analytics dimension: the account
 * alias and every portfolio/instrument id would each mint their own value, and ids in a telemetry payload
 * are a privacy question we don't need to answer to know that Kelly gets used next to the dashboard.
 */

import type { Params } from "react-router-dom"

// Fallback for a caller with no params (and for a pathname the router did not match). Substituted rather than
// dropped, so `/acme/portfolio/1234/risk` and `/acme/portfolio/9/risk` are one route.
const ID_SEGMENT = /^(?:\d+|[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}|[0-9a-f]{24})$/i

/** A splat (`*`) matches a whole tail rather than one segment, so its own name would read oddly per-segment. */
const SPLAT_NAME = "path"

/**
 * A pathname segment in the representation the router's PARAMS are in. `location.pathname` stays percent-encoded
 * while `matchRoutes` matches against a decoded copy, so the two disagree for any value that needed encoding —
 * and an unmatched parameter was emitted verbatim. Mirrors the router's own `decodePath`, which re-escapes an
 * encoded slash because a real one separates segments. Malformed input decodes to itself, as it does there.
 */
function decodeSegment(segment: string): string {
  try {
    return decodeURIComponent(segment).replace(/\//g, "%2F")
  } catch {
    return segment
  }
}

/**
 * Every segment the router filled in from a route parameter, mapped to the name it was filled from. Driven by
 * the ROUTE's own parameters rather than by what a value looks like, so a segment is masked because it is a
 * parameter — not because it happened to match an id shape. That is what keeps an opaque credential
 * (`user-activation/:token`) out of the payload: the format heuristic below never matched one.
 */
function buildParamSegmentNames(params: Params): ReadonlyMap<string, string> {
  const names = new Map<string, string>()
  for (const [name, value] of Object.entries(params)) {
    if (!value) continue
    // A splat's value spans segments; mask each of them.
    for (const segment of value.split("/").filter(Boolean)) {
      names.set(segment, name === "*" ? SPLAT_NAME : name)
    }
  }
  return names
}

/**
 * @param pathname `location.pathname`
 * @param params the matched route's parameters (`useMatches()`), so parameter values are masked by name
 * @returns e.g. `/:account/portfolio/:portfolioId/risk`, or `/` for a pathname with no segments
 */
export function routeTemplate(pathname: string, params: Params = {}): string {
  const segments = pathname.split("/").filter(Boolean)
  if (segments.length === 0) return "/"
  const paramNames = buildParamSegmentNames(params)
  // The first segment is always the account alias in this app's routes (`/:accountValue/...`), and an alias
  // is a tenant name — never emit it. Named for the property rather than the route's parameter so the value
  // stays stable if the route is renamed.
  const template = segments.map((segment, index) => {
    if (index === 0) return ":account"
    const decoded = decodeSegment(segment)
    const paramName = paramNames.get(decoded)
    if (paramName != null) return `:${paramName}`
    // Percent-encoding at all means this is a value, not a route name — every static segment in this app is a
    // plain ASCII slug. Masked rather than emitted, so a value still reaching here is never in the payload:
    // an unmatched pathname, a malformed sequence (which decodes to itself), or a decoded form that differs
    // from the router's by Unicode normalisation. Cheaper to be certain than to enumerate those.
    if (segment.includes("%")) return ":id"
    return ID_SEGMENT.test(decoded) ? ":id" : decoded
  })
  return `/${template.join("/")}`
}
